import { Box, IconButton, useTheme, Switch, Typography, useMediaQuery, Button, InputBase, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Paper, Popper,  ClickAwayListener  } from "@mui/material";
import { useContext, useState, useEffect, useRef  } from "react";
import { ColorModeContext, tokens } from "../../theme";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useApplicationMode } from "../../common/ApplicationModeContext"; 
import { useUserCount } from "../../common/UserCountContext"; 
import { useAccount } from "../../common/AccountContext"; 
import { useUser } from "../../common/UserContext"; 
import { useLocation, useNavigate } from "react-router-dom";

import { PLANS } from "../../planConfig";

import { ref, onValue, off } from "firebase/database";

import { Link } from 'react-router-dom';

import { realtimeDb } from "../../firebaseConfig";

const Topbar = ({ setSearchTerm }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const userCount = useUserCount();
  const { accountStatus, accountSubscriptionPlan, accountSubscriptionType, accountCreatedDate } = useAccount();
  const { appUserFirstName, appUserLastName, appUserEmail, appUserRole } = useUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); 
  const { setIsTestMode, isTestMode } = useApplicationMode();

  // NEW STATES for Dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [openTrialExpiredDialog, setOpenTrialExpiredDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', message: '', buttons: [] });

  const [openPopper, setOpenPopper] = useState(false);
  const anchorRef = useRef(null);


  useEffect(() => {

    /*
      It uses Realtime Database:
        ✅ No meaningful cost
        ✅ No writes counted
        ✅ No stored data buildup

        ✔ The Realtime Database .info/serverTimeOffset solution solves both write overhead and cost concerns.
        ✔ You get effectively free, accurate server time.
        ✔ It scales safely even if your SaaS grows big.

    */
    
      if (accountSubscriptionType !== "trial" || !accountCreatedDate) return;
    
      const keepAliveRef = ref(realtimeDb, "/keepalive");
      const connectedRef = ref(realtimeDb, ".info/connected");
      const offsetRef = ref(realtimeDb, ".info/serverTimeOffset");
    
      const handleOffset = (snapshot) => {
        const offset = snapshot.val() || 0;
        const estimatedServerTimeMs = Date.now() + offset;
    
        const serverTime = new Date(estimatedServerTimeMs);
        const createdTime = accountCreatedDate.toDate();
        const daysElapsed = Math.floor((serverTime - createdTime) / (1000 * 60 * 60 * 24));
    
        if (daysElapsed >= PLANS.trialPeriodDurationInDays) {
          setDialogContent({
            title: "Your Free Trial Has Ended",
            message: (
              <>
                <p>Your {PLANS.trialPeriodDurationInDays}-day free trial has ended. To continue enjoying full access, please subscribe below.</p>
                <p>All your records will remain safely available after you subscribe.</p>
              </>
            ),
            buttons: [
              {
                label: "Subscribe Now",
                action: () => navigate("/pricing"),
                color: "primary"
              }
            ]
          });
          setOpenTrialExpiredDialog(true);
        }
      };
    
      // Start dummy keep-alive listener
      onValue(keepAliveRef, () => {});
    
      // Check if Realtime Database connection is active
      const handleConnected = (snapshot) => {
        if (snapshot.val() === true) {
          //console.log("Realtime Database connection is active");
          
          onValue(offsetRef, handleOffset);
        } else {
          //console.warn("Realtime Database connection is not active");
          
        }
      };
    
      onValue(connectedRef, handleConnected);
    
      return () => {
        off(keepAliveRef);
        off(connectedRef, handleConnected);
        off(offsetRef, handleOffset);
      };
    }, [accountSubscriptionType, accountCreatedDate, navigate]);
        
  

  useEffect(() => {
    if (!accountStatus || !userCount || !accountSubscriptionPlan) return;

    let maxPlanUserAccounts = 0;

    if (accountStatus !== "canceled")
    {
      maxPlanUserAccounts = PLANS.planAdditionaInfo[accountSubscriptionPlan].maxUserAccounts;
    } else 
    {
       maxPlanUserAccounts = 0; //Setting to zero if it has been canceled
    }

    if (userCount > maxPlanUserAccounts) {

      if (appUserRole !== "admin") { 

        if (accountStatus !== "canceled")
        {     
          setDialogContent({
            title: "Action Required by Administrator",
            message: (
              <>
                <p>Your organization has made changes to its subscription plan that require immediate action from your system administrator.</p>
                <p>Please notify your administrator immediately. We apologize for any inconvenience and appreciate your understanding.</p>
              </>
            ),
            buttons: [
              { label: "OK", action: () => navigate("/logout"), color: "primary" }
            ]
          });

        } else
        {
          setDialogContent({
                title: "Canceled Account",
                message: (
                  <>
                    <p>Your subscription has been canceled, and access to your organization's features is currently paused.</p>
                    <p>Please contact your administrator for further information.</p>
                  </>
                ),
                buttons: [
                  { 
                    label: "Continue", 
                    action: () => {
                      setOpenDialog(false);
                      navigate("/logout");
                    }, 
                    color: "secondary" 
                  }
                ]
              });
        }

        setOpenDialog(true);

      } else {
        if (location.pathname !== "/useraccounts") // I won't show the message if they are already in the /useraccounts, otherwise it shows the dialog twice
        {
          if (accountStatus !== "canceled")
          {     
              setDialogContent({
                title: "User Account Limit Exceeded",
                message: (
                  <>
                    <p>Your current user count is <strong>{userCount}</strong>, but your subscription plan allows a maximum of <strong>{maxPlanUserAccounts}</strong> user accounts.</p>
                    <p>This situation typically occurs after a subscription downgrade. To resolve this, please visit the User Accounts section and reduce the number of user accounts to meet the plan limit. Alternatively, you can upgrade to a plan that accommodates your current number of users.</p>
                    <p>We apologize for the inconvenience. You only need to complete this action once after downgrading.</p>
                  </>
                ),
                buttons: [
                  { 
                    label: "Take me to User Accounts", 
                    action: () => {
                      setOpenDialog(false);
                      navigate("/useraccounts");
                    }, 
                    color: "primary" 
                  },
                  { 
                    label: "Upgrade my Plan", 
                    action: () => {
                      setOpenDialog(false);
                      navigate("/pricing");
                    }, 
                    color: "secondary" 
                  }
                ]
              });
          
          } else
          {
              setDialogContent({
                title: "Canceled Account",
                message: (
                  <>
                    <p>Your subscription has been canceled, and access to your organization's features is currently paused.</p>
                    <p>To resume your subscription and restore full access, click the <strong>"Renew my Subscription"</strong> button below.</p>
                  </>
                ),
                buttons: [
                  { 
                    label: "Renew my Subscription", 
                    action: () => {
                      setOpenDialog(false);
                      navigate("/pricing");
                    }, 
                    color: "secondary" 
                  }
                ]
              });
            
          }

          setOpenDialog(true);
        }
      }
    }

    
    if (accountStatus === "past_due")
    {
        if (location.pathname !== "/subscriptions") // I won't show the message if they are already in the /subscriptions, otherwise it shows the dialog twice
        {

          if (appUserRole === "admin") { 

                setDialogContent({
                        title: "Account Access Restricted",
                        message: (
                          <>
                            <p>We couldn’t process your recent payment, so access to some features has been limited.</p>
                            <p>To restore full access, please update your payment method by clicking the <br></br> <strong>"Go to Subscriptions"</strong> button below.</p>
                          </>
                        ),
                        buttons: [
                          { 
                            label: "Go to Subscriptions", 
                            action: () => {
                              setOpenDialog(false);
                              navigate("/subscriptions");
                            }, 
                            color: "secondary" 
                          }
                        ]
                      });
            
            } else 
            {

                setDialogContent({
                        title: "Access Restricted",
                        message: (
                          <>
                            <p>Access to certain features has been temporarily disabled for your organization.</p>
                            <p>Please contact your organization administrator to restore access.</p>
                          </>
                        ),
                        buttons: [
                          { 
                            label: "Continue", 
                            action: () => {
                              setOpenDialog(false);
                              navigate("/logout");
                            }, 
                            color: "secondary" 
                          }
                        ]
                      });
            }

            setOpenDialog(true);
        }

    }

  }, [accountStatus, userCount, accountSubscriptionPlan, appUserRole, navigate]);

  
  const handleTogglePopper = () => {
    setOpenPopper((prev) => !prev);
  };

  const handleClosePopper = () => {
    setOpenPopper(false);
  };

  const handleDialogClose = () => {
    if (appUserRole !== "admin") {
      navigate("/logout");
    } else {
      setOpenDialog(false);
      navigate("/useraccounts");
    }
  };

  const handleTrialExpiredDialogClose = () => {
     // navigate("/pricing");
  };

  const handleChange = (event) => {
    setIsTestMode(event.target.checked); 
  };
  
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value); 
  };

  const handleUpgradeClick = () => {
    navigate("/pricing");
  };

  return (
    <Box>
      {/* FORCED DIALOG */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        disableEscapeKeyDown
        disableBackdropClick
      >
        <DialogTitle>{dialogContent.title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {dialogContent.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", flexDirection: "column", gap: 2, pb: 3 }}>
          {dialogContent.buttons.map((btn, index) => (
            <Button
              key={index}
              variant="contained"
              onClick={btn.action}
              sx={{
                backgroundColor: btn.color === "primary" ? "#1976d2" : "#db5500",
                color: "#fff",
                fontWeight: "bold",
                width: "80%",
                textTransform: "none",
                '&:hover': {
                  backgroundColor: btn.color === "primary" ? "#115293" : "#e66a00",
                }
              }}
            >
              {btn.label}
            </Button>
          ))}
        </DialogActions>
      </Dialog>

      
      {/* TRIAL DIALOG */}
      <Dialog
        open={openTrialExpiredDialog}
        onClose={handleTrialExpiredDialogClose}
        disableEscapeKeyDown
        disableBackdropClick
      >
        <DialogTitle>{dialogContent.title}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {dialogContent.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", flexDirection: "column", gap: 2 }}>
          {dialogContent.buttons.map((btn, index) => (
            <Button
              key={index}
              variant="contained"
              onClick={btn.action}
              sx={{
                backgroundColor: btn.color === "primary" ? "#1976d2" : "#db5500",
                color: "#fff",
                fontWeight: "bold",
                width: "80%",
                textTransform: "none",
                '&:hover': {
                  backgroundColor: btn.color === "primary" ? "#115293" : "#e66a00",
                }
              }}
            >
              {btn.label}
            </Button>
          ))}
        </DialogActions>
      </Dialog>


      {/* EXISTING CONTENT */}

      {/* Test Mode */}
      {location.pathname !== "/useraccounts"  && location.pathname !== "/subscriptions"  && location.pathname !== "/unauthorized" &&
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} style={{ backgroundColor: isTestMode ? "#cc4b00" : "transparent" }}>
          <Box flex={1} display="flex" justifyContent="center" style={{ color: isTestMode ? "#FFFFFF" : "" }}>
            {isTestMode && (
              <Typography variant="h5">
                {isMobile ? "You are in test mode" : <>You are in <Typography component="span" fontWeight="bold">test mode</Typography>. You are using <Typography component="span" fontWeight="bold">test data</Typography>. No real transactions will be processed.</>}
              </Typography>
            )}
          </Box>

          <table>
            <tr>
              <td>
                <Typography variant="h5" style={{ fontWeight: 700, color: isTestMode ? "#FFFFFF" : "" }}>Test mode</Typography>
              </td>
              <td>
                <Switch
                  checked={isTestMode}
                  onChange={handleChange}
                  color="#000000"
                />
              </td>
            </tr>
          </table>
        </Box>
      }

      {/* Upgrade Button */}
      {accountSubscriptionType === "trial" && 
      <Box display="flex" justifyContent="center" mt={2}>
        <Button
          variant="contained"
          onClick={handleUpgradeClick}
          sx={{
            backgroundColor: "#db5500",
            color: "#fff",
            fontWeight: "bold",
            fontSize: "20px",
            textTransform: "none",
            '&:hover': {
              backgroundColor: "#e66a00",
            },
          }}
        >
          Unlock Full Access
        </Button>
      </Box>
      }

      {/* Top bar content */}
      <Box display="flex" justifyContent="space-between" p={2}>
        {/* SEARCH BAR */}
        <Box display="flex" backgroundColor={colors.primary[400]} borderRadius="3px">
          <InputBase sx={{ ml: 2, flex: 1 }} placeholder="Search" onChange={handleSearchChange} />
          <IconButton type="button" sx={{ p: 1 }}>
            <SearchIcon />
          </IconButton>
        </Box>

        {/* ICONS */}
        <Box display="flex">
          <IconButton onClick={colorMode.toggleColorMode}>
            {theme.palette.mode === "dark" ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
          </IconButton>
          {/*
          <IconButton>
            <NotificationsOutlinedIcon />
          </IconButton>
          */}
          <Link to="/subscriptions">
            <IconButton>
              <SettingsOutlinedIcon />
            </IconButton>
          </Link>
          <IconButton ref={anchorRef} onClick={handleTogglePopper}>
            <PersonOutlinedIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Popper (callout) */}
      <Popper open={openPopper} anchorEl={anchorRef.current} placement="bottom-end" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={handleClosePopper}>
          <Paper elevation={3} sx={{ p: 2, minWidth: 250, border: '1px solid #8a8a8a' }}>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ pt:1, fontSize:16 }}>
              {appUserFirstName} {appUserLastName}
            </Typography>
            
           
            <Typography variant="body2" color="text.secondary" sx={{ wordWrap: "break-word", pt:2, fontSize:16 }}>
              {appUserEmail}
            </Typography>
            
            <br></br>
            
            <Button
              variant="outlined"
              fullWidth
              color="primary" sx={{ fontSize:15, fontWeight:'bold', backgroundColor: colors.greenAccent[600], color: colors.grey[100], '&:hover': { backgroundColor: colors.greenAccent[800] } }}
              onClick={() => navigate("/logout")}
            >
              Log Out
            </Button>
            <Box sx={{pt:2}}></Box>
          </Paper>
        </ClickAwayListener>
      </Popper>


    </Box>
  );
};

export default Topbar;
